import { DestroyRef, Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, isObservable, Observable, skip } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Route, Router } from '@angular/router';
import { Breadcrumb } from '../model/breadcrumb';
import { RoleSpecificRouteDataItem } from '@markant-platform/shared/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { TranslocoService } from '@jsverse/transloco';
import { StaticRole } from '@markant-platform/shared/user/model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly breadcrumbSubject = new BehaviorSubject<Breadcrumb[]>([]);
  public breadcrumbs$ = this.breadcrumbSubject.asObservable();
  readonly NAVIGATION_PREFIX = 'dashboardOverview.navigation.';
  private userRoles: StaticRole[] = [];

  constructor(
    private readonly router: Router,
    private readonly translocoService: TranslocoService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly destroyRef: DestroyRef
  ) {
    this.authenticatedUserService
      .getUserRoles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(roles => (this.userRoles = roles.map(({ name }) => name as StaticRole)));

    this.router.events
      ?.pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.buildBreadcrumb());

    this.translocoService.langChanges$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildBreadcrumb());
  }

  public async buildBreadcrumb(): Promise<void> {
    const root = this.router.routerState.root;
    let breadcrumbs: Breadcrumb[] = [];
    await this.addBreadcrumb(root.firstChild, breadcrumbs);

    // Filter doubled entries
    breadcrumbs = breadcrumbs.filter((obj, index) => {
      // TODO: Check if doubled entries can be avoided up front. It seems that some routes
      // which are getting called from dashboard-feature-dashboard-overview-routing.module.ts
      // create doubled entries due to an (to date) unknown reason.
      return index === breadcrumbs.findIndex(o => obj.label === o.label);
    });

    this.breadcrumbSubject.next(breadcrumbs);
  }

  private async addBreadcrumb(route: ActivatedRoute | null, breadcrumbs: Breadcrumb[]): Promise<void> {
    if (route) {
      const routeConfig = route.routeConfig;
      const translationKey = routeConfig?.data?.['translationKey'];
      const roleSpecificData = routeConfig?.data?.['roleSpecificData'];
      const url = routeConfig?.path ? this.createUrl(breadcrumbs, routeConfig, routeConfig.path, roleSpecificData) : '';

      if (translationKey && url && !routeConfig?.resolve?.['subBreadcrumbs']) {
        const label = await firstValueFrom(
          this.translocoService.selectTranslate(`${this.NAVIGATION_PREFIX + translationKey}.label`)
        );

        breadcrumbs.push({ label, url });
      }

      await this.handleSubBreadcrumbs(route, breadcrumbs, url);

      if (route.firstChild !== null) {
        await this.addBreadcrumb(route.firstChild, breadcrumbs);
      }
    }
  }

  private async handleSubBreadcrumbs(route: ActivatedRoute, breadcrumbs: Breadcrumb[], url: string): Promise<void> {
    // subBreadcrumbs can be an array or an observable of an array
    let subBreadcrumbs: Breadcrumb[] | Observable<Breadcrumb[]> = route.snapshot?.data['subBreadcrumbs'] || [];
    if (isObservable(subBreadcrumbs)) {
      subBreadcrumbs = await firstValueFrom(subBreadcrumbs);
    }

    for (const subBreadcrumb of subBreadcrumbs) {
      const label = subBreadcrumb.translateLabel
        ? await firstValueFrom(
            this.translocoService.selectTranslate(subBreadcrumb.translateLabel, subBreadcrumb.translateParams)
          )
        : subBreadcrumb.label;

      const urlPath = subBreadcrumb.url ? this.appendPathToUrl(url, subBreadcrumb.url) : '';

      breadcrumbs.push({ label, url: urlPath });
    }
  }

  private appendPathToUrl(url: string, newPath: string): string {
    return url.split('/').some(path => path === newPath) ? url : `${url}/${newPath}`;
  }

  private createUrl(
    breadcrumbs: Breadcrumb[],
    routeConfig: Route,
    routerPath: string,
    roleSpecificData: Record<string, RoleSpecificRouteDataItem>
  ): string {
    let url: string;
    let concatinatedUrl = '';

    if (breadcrumbs.length) {
      concatinatedUrl = breadcrumbs[breadcrumbs.length - 1].url;
    }

    if (roleSpecificData) {
      const dataItem = Object.entries(roleSpecificData).find(([key]) => this.userRoles.some(role => role === key))?.[1];
      routerPath = dataItem ? `${routerPath}/${dataItem.route}` : routerPath;
    }

    // Only concat url if no resolver is involved otherwise it will be handled by the subBreadcrumbs data attribute
    if (!routeConfig.resolve?.['subBreadcrumbs']) {
      url = concatinatedUrl ? `${concatinatedUrl}/${routerPath}` : routerPath;
    } else {
      url = concatinatedUrl;
    }
    return url.startsWith('/') ? url : `/${url}`;
  }
}
